import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import withStyles from '@material-ui/core/styles/withStyles';
import Link from '@material-ui/core/Link';
import { withRouter } from 'react-router-dom';
import * as actions from 'Store/actions';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { injectStripe } from 'react-stripe-elements';
import CardSection from './CardSection';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import { Checkbox } from '@material-ui/core';
import Loading from 'Components/Loading';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useForm, Controller } from 'react-hook-form';
import * as Regex from '../../regex';
import FormTextField from 'Components/FormTextField';
import { isEmpty } from 'lodash';
import usStates from 'Constants/usStates';
import * as UserHelpers from 'Helpers/user';
import queryString from 'query-string';
import CenteredSpinner from '../CenteredSpinner';

const pageStyles = theme => ({
  link: {
    textDecoration: 'underline',
    cursor: 'pointer',
    color: 'inherit',
  },
  taken: {
    color: 'red',
  },
  checkoutForm: {
    display: 'flex',
    flexDirection: 'column',
  },
  button: {
    alignSelf: 'flex-start',
    marginTop: '90px',
    [theme.breakpoints.down('sm')]: {
      marginTop: 10,
    },
  },
  proceedButton: {
    color: '#fff',
  },
  selectFormControl: {
    width: '50%',
  },
  selectLabel: {
    top: '-6px',
  },
  creditCards: {
    height: '30px',
    display: 'block',
  },
  iAgree: {
    marginBottom: 170,
    marginTop: 60,
    [theme.breakpoints.down('sm')]: {
      marginTop: 20,
      marginBottom: 20,
    },
  },
  selectedPlanContainer: {
    border: '3px black solid',
    padding: 24,
    marginBottom: 48,
  },
  selectedPlanText: {
    marginBottom: 0,
  },
  spinner: {
    marginBottom: '40px',
  },
  switchTextContainer: {
    marginTop: '30px',
  },
});

const CheckoutForm = ({ planData, getPlanData, ...props }) => {
  const user = props.user || {};
  const { classes, checkoutData } = props;

  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTosApproved, setIsTosApproved] = useState(false);

  const [isShippingAddressSameAsBillingAddress, setIsShippingAddressSameAsBillingAddress] = useState(true);

  useEffect(() => {
    setIsComplete(props.checkoutData.complete);
    setIsLoading(props.checkoutData.processing);
  }, [props.checkoutData.complete, props.checkoutData.processing]);

  useEffect(() => {
    getPlanData();
  }, [getPlanData]);

  const { register, handleSubmit, errors, getValues, control } = useForm({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      billingAddressLine1: user.address1,
      billingAddressLine2: user.address2,
      billingCity: user.city,
      billingState: user.state,
      billingZipCode: user.zipcode,
      billingCountry: user.country || 'US',
      shippingAddressLine1: user.address1,
      shippingAddressLine2: user.address2,
      shippingCity: user.city,
      shippingState: user.state,
      shippingZipCode: user.zipcode,
      shippingCountry: user.country || 'US',
    },
  });

  let {
    firstName,
    lastName,
    email,
    password,
    confirmPassword,
    billingAddressLine1,
    billingAddressLine2,
    billingCity,
    billingState,
    billingZipCode,
    billingCountry,
    shippingAddressLine1,
    shippingAddressLine2,
    shippingCity,
    shippingState,
    shippingZipCode,
    shippingCountry,
  } = getValues();

  const openTosWindow = () => {
    window.open(`${window.location.origin}/TOS?hasNav=false`, 'tos', 'width=500,height=800');
  };

  const openLoginModal = () => {
    props.setActiveModal('login');
  };

  const onSubmitForm = async ev => {
    setIsLoading(true);
    //<!-- Event snippet for Website sale conversion page -->

    window.gtag('event', 'conversion', {
      send_to: 'AW-643218609/1DU_CPm0y9ABELH52rIC',
      value: 15.0,
      currency: 'USD',
      transaction_id: '',
    });

    // if not logged in then try to register
    // if logged in then submit

    const { itemDraft } = props;

    let userId = props.user ? props.user.uid : '';

    if (user.firstName) {
      firstName = user.firstName;
      lastName = user.lastName;
      email = user.email;
    }

    if (!props.user) {
      try {
        const registrationRequest = await props.registerWithFirebase({
          email,
          password,
          firstName,
          lastName,
          address1: billingAddressLine1,
          address2: billingAddressLine2,
          city: billingCity,
          state: billingState,
          zipcode: billingZipCode,
          country: billingCountry,
          phone: user.phone || get(itemDraft, 'info.phone'),
        });

        userId = registrationRequest.payload.uid;
      } catch (err) {
        console.warn('Registration error: ', err);
        alert(err);
      }
    }

    let token;

    try {
      // this is done here instead of an actions because this prop
      // is only available here due to how stripe works (supplying info from HOC)
      const tokenObj = await props.stripe.createToken({
        type: 'card',
        name: `${firstName} ${lastName}`,
        address_line1: billingAddressLine1,
        address_line2: billingAddressLine2,
        address_state: billingState,
        address_zip: billingZipCode,
        email,
      });
      token = tokenObj.token;
    } catch (error) {
      console.warn('Token error is ', error);
      setIsLoading(false);
    }

    if (!token) {
      console.warn('Token object does not exist: ', token);
      setIsLoading(false);
      return;
    }

    const _items = itemDraft.order.map(item => {
      return {
        ...itemDraft.info,
        ...item,
      };
    });

    const checkoutData = {
      tokenId: token.id,
      userInfo: {
        email,
        firstName,
        lastName,
        phone: user.phone || get(itemDraft, 'info.phone'),
        address1: billingAddressLine1,
        address2: billingAddressLine2,
        city: billingCity,
        state: billingState,
        zipcode: billingZipCode,
        country: billingCountry,
        userId,
        sameShip: isShippingAddressSameAsBillingAddress,
        shipping_address1: shippingAddressLine1,
        shipping_address2: shippingAddressLine2,
        shipping_country: shippingCountry,
        shipping_zipcode: shippingZipCode,
        shipping_state: shippingState,
        shipping_city: shippingCity,
      },
      planId: get(queryString.parse(window.location.search), 'planId'),
      items: _items,
      userId,
    };

    //TODO: Perhaps update firebase user info on checkout
    props.checkout(checkoutData);
    // Scroll top is needed because otherwise user is looking at blank hidden form
    window.scrollTo(0, 0);
  };

  if (isComplete) {
    return (
      <div style={{ marginTop: 50 }}>
        <h1>Welcome to the Ownerfy Community!</h1>
        <p>Your order Number is {checkoutData.orderId}.</p>
        <p>You will receive an email confirmation soon.</p>
        <p>Average shipping time is 1 - 2 weeks.</p>
        <p>Visit the dashboard to edit your tag information.</p>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.proceedButton}
          component={Link}
          href="/dashboard"
          style={{ marginTop: 30, textDecoration: 'none' }}
        >
          Proceed to Dashboard
        </Button>
      </div>
    );
  }

  const getEmailErrorMessage = () => {
    if (errors.email) {
      return errors.email.type === 'isTaken' ? (
        <>
          This email is taken. Please{' '}
          <span className={classes.link} onClick={openLoginModal}>
            (Log in)
          </span>{' '}
          or use another.
        </>
      ) : (
        errors.email.message
      );
    }

    return false;
  };

  const renderUserInformationFields = () => {
    return (
      <div>
        <Typography variant="h5">
          Create Account{' '}
          <span className={classes.link} onClick={openLoginModal}>
            (or log in)
          </span>
        </Typography>

        <FormTextField
          label="First Name"
          name="firstName"
          required
          error={!!errors.firstName}
          helperText={errors.firstName && errors.firstName.message}
          inputRef={register({ required: 'Provide your first name' })}
          placeholder="Enter First Name"
          style={{ marginRight: 20 }}
        />

        <FormTextField
          label="Last Name"
          name="lastName"
          required
          error={!!errors.lastName}
          helperText={errors.lastName && errors.lastName.message}
          inputRef={register({ required: 'Provide your last name' })}
          placeholder="Enter Last Name"
        />

        <FormTextField
          fullWidth
          label="Email"
          name="email"
          required
          error={!!errors.email}
          helperText={getEmailErrorMessage()}
          helperTextComponent="div"
          inputRef={register({
            required: 'Provide the email',
            pattern: {
              value: Regex.email,
              message: 'Provide a valid email address',
            },
            validate: {
              isTaken: async value => {
                //const isEmailTaken = await debouncedIsEmailTakenMethod(value);
                const isEmailTaken = await UserHelpers.isEmailTaken(value);
                if (isEmailTaken) {
                  return 'Email is taken';
                }
              },
            },
          })}
          placeholder="Enter Email"
        />

        <Box display="flex">
          <FormTextField
            label="Password"
            name="password"
            type="password"
            required
            error={!!errors.password}
            helperText={errors.password && errors.password.message}
            inputRef={register({
              required: 'Provide your password',
              minLength: {
                value: 8,
                message: 'Password must be longer than 8 characters',
              },
              validate: value => {
                if (confirmPassword && value !== confirmPassword) {
                  return 'Passwords need to match';
                }
              },
            })}
            placeholder="Enter Password"
            style={{ marginRight: 20 }}
          />

          <FormTextField
            label="Confirm"
            name="confirmPassword"
            type="password"
            required
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword && errors.confirmPassword.message}
            inputRef={register({
              required: 'Confirm your password',
              validate: value => {
                if (password && value !== password) {
                  return 'Passwords need to match';
                }
              },
            })}
            placeholder="Enter Password"
          />
        </Box>
      </div>
    );
  };

  const renderBillingFields = () => {
    return (
      <div>
        <Typography variant="h5" style={{ marginTop: 100 }}>
          Billing Address
        </Typography>

        <FormTextField
          fullWidth
          label="Address Line 1"
          name="billingAddressLine1"
          required
          error={!!errors.billingAddressLine1}
          helperText={errors.billingAddressLine1 && errors.billingAddressLine1.message}
          inputRef={register({ required: 'This field is required' })}
          placeholder="Enter Address Line 1"
        />

        <FormTextField
          fullWidth
          label="Address Line 2"
          name="billingAddressLine2"
          inputRef={register}
          placeholder="Enter Address Line 2"
        />

        <Box display="flex" justifyContent="space-between" alignItems="flex-end">
          <FormTextField
            label="City"
            name="billingCity"
            required
            error={!!errors.billingCity}
            helperText={errors.billingCity && errors.billingCity.message}
            inputRef={register({ required: 'This field is required' })}
            placeholder="Enter City"
            style={{ marginRight: 20 }}
          />

          <FormControl className={classes.selectFormControl} error={!!errors.billingState}>
            <InputLabel id="billing-state-label" className={classes.selectLabel}>
              State
            </InputLabel>

            <Controller
              as={
                <Select defaultValue="">
                  {usStates.map(stateName => (
                    <MenuItem value={stateName} key={stateName}>
                      {stateName}
                    </MenuItem>
                  ))}
                </Select>
              }
              name="billingState"
              rules={{ required: 'This field is required' }}
              control={control}
              labelId="billing-state-label"
            />

            {errors.billingState && <FormHelperText>{errors.billingState.message}</FormHelperText>}
          </FormControl>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="flex-end">
          <FormTextField
            label="Zip Code"
            name="billingZipCode"
            required
            error={!!errors.billingZipCode}
            helperText={errors.billingZipCode && errors.billingZipCode.message}
            inputRef={register({ required: 'This field is required' })}
            placeholder="Enter Zip Code"
            style={{ marginRight: 20 }}
          />

          <FormControl className={classes.selectFormControl} error={!!errors.billingCountry}>
            <InputLabel id="billing-country-label" className={classes.selectLabel}>
              Country
            </InputLabel>

            <Controller
              as={
                <Select defaultValue="US">
                  <MenuItem value="US">United States</MenuItem>
                </Select>
              }
              name="billingCountry"
              rules={{ required: 'This field is required' }}
              control={control}
              labelId="billing-country-label"
            />

            {errors.billingCountry && <FormHelperText>{errors.billingCountry.message}</FormHelperText>}
          </FormControl>
        </Box>
      </div>
    );
  };

  const renderShippingFields = () => {
    if (!isShippingAddressSameAsBillingAddress) {
      return (
        <div>
          <FormTextField
            fullWidth
            label="Address Line 1"
            name="shippingAddressLine1"
            required
            error={!!errors.shippingAddressLine1}
            helperText={errors.shippingAddressLine1 && errors.shippingAddressLine1.message}
            inputRef={register({ required: 'This field is required' })}
            placeholder="Enter Address Line 1"
          />

          <FormTextField
            fullWidth
            label="Address Line 2"
            name="shippingAddressLine2"
            inputRef={register}
            placeholder="Enter Address Line 2"
          />

          <Box display="flex" justifyContent="space-between" alignItems="flex-end">
            <FormTextField
              label="City"
              name="shippingCity"
              required
              error={!!errors.shippingCity}
              helperText={errors.shippingCity && errors.shippingCity.message}
              inputRef={register({ required: 'This field is required' })}
              placeholder="Enter City"
              style={{ marginRight: 20 }}
            />

            <FormControl className={classes.selectFormControl} error={!!errors.shippingState}>
              <InputLabel id="shipping-state-label" className={classes.selectLabel}>
                State
              </InputLabel>

              <Controller
                as={
                  <Select defaultValue="">
                    {usStates.map(stateName => (
                      <MenuItem value={stateName} key={stateName}>
                        {stateName}
                      </MenuItem>
                    ))}
                  </Select>
                }
                name="shippingState"
                rules={{ required: 'This field is required' }}
                control={control}
                labelId="shipping-state-label"
              />

              {errors.shippingState && <FormHelperText>{errors.shippingState.message}</FormHelperText>}
            </FormControl>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="flex-end">
            <FormTextField
              label="Zip Code"
              name="shippingZipCode"
              required
              error={!!errors.shippingZipCode}
              helperText={errors.shippingZipCode && errors.shippingZipCode.message}
              inputRef={register({ required: 'This field is required' })}
              placeholder="Enter Zip Code"
              style={{ marginRight: 20 }}
            />

            <FormControl className={classes.selectFormControl} error={!!errors.shippingCountry}>
              <InputLabel id="shipping-country-label" className={classes.selectLabel}>
                Country
              </InputLabel>

              <Controller
                as={
                  <Select defaultValue="US">
                    <MenuItem value="US">United States</MenuItem>
                  </Select>
                }
                name="shippingCountry"
                rules={{ required: 'This field is required' }}
                control={control}
                labelId="shipping-country-label"
              />

              {errors.shippingCountry && <FormHelperText>{errors.shippingCountry.message}</FormHelperText>}
            </FormControl>
          </Box>
        </div>
      );
    }

    return false;
  };

  function getPlanSwitchText() {
    const { planId, previousPlanId } = queryString.parse(window.location.search);
    console.log('typeof planId', typeof planId);
    const isUpgrading = planId > previousPlanId;

    console.log('isUpgrading', isUpgrading);

    if (isUpgrading) {
      return 'Your first month will be pro-rated and you will be billed the new rate at the beginning of the next billing cycle.';
    } else {
      return 'Your current plan will be active until the end of your billing cycle and you will be billed the new rate at the beginning of the next billing cycle.';
    }
  }

  const renderSelectedPlan = () => {
    const { planId, previousPlanId } = queryString.parse(window.location.search);

    if (!planData.availablePlans.length) {
      return <CenteredSpinner className={classes.spinner} />;
    }

    const selectedPlan = planData.availablePlans.find(plan => plan.planId === planId);

    if (!selectedPlan) {
      return false;
    }

    return (
      <div className={classes.selectedPlanContainer}>
        <Typography variant="h5">Selected Plan</Typography>
        <p className={classes.selectedPlanText}>
          {selectedPlan.name} - ${selectedPlan.price} per month
        </p>

        {previousPlanId && (
          <div className={classes.switchTextContainer}>
            <Typography variant="h5">Note</Typography>
            <p className={classes.selectedPlanText}>{getPlanSwitchText()}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {isLoading && (
        <Loading
          type={'bubbles'}
          color={'#ccc'}
          height={'80px'}
          width={'80px'}
          containerHeight={'70vh'}
          containerWidth={'100%'}
          message={'Completing Order...'}
        />
      )}
      {/* Elements still need to exist on page to get values */}
      <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
        <Typography variant="h1">Checkout</Typography>

        {renderSelectedPlan()}

        <form onSubmit={handleSubmit(onSubmitForm)}>
          {isEmpty(user) ? (
            renderUserInformationFields()
          ) : (
            <Typography variant="h6" style={{ marginBottom: '-60px' }}>
              Logged in as {user.firstName}{' '}
              <span className={classes.link} onClick={props.logoutUser}>
                (Log out)
              </span>
            </Typography>
          )}

          {renderBillingFields()}

          <Typography variant="h5" style={{ marginTop: 100 }}>
            Shipping Address
          </Typography>

          <FormControlLabel
            value="end"
            control={
              <Checkbox
                checked={isShippingAddressSameAsBillingAddress}
                name="isShippingAddressSameAsBillingAddress"
                onChange={() => setIsShippingAddressSameAsBillingAddress(!isShippingAddressSameAsBillingAddress)}
                value="checkedA"
                inputProps={{
                  'aria-label': 'primary checkbox',
                }}
              />
            }
            label="Same As Billing Address?"
            labelPlacement="end"
            style={{ marginTop: 30 }}
          />
          {renderShippingFields()}

          <div style={{ marginTop: 70, marginBottom: 15 }}>
            <CardSection />
            {/* <CreditCard /> */}
            {/* <StripeInjectedCreditCard /> */}
          </div>

          <img className={classes.creditCards} src="img/creditcards.jpg" />

          <FormControlLabel
            value="end"
            className={classes.iAgree}
            control={
              <Checkbox
                checked={isTosApproved}
                name="tosChecked"
                onChange={() => setIsTosApproved(!isTosApproved)}
                value="checkedT"
              />
            }
            label={
              <div>
                I agree to the{' '}
                <Link onClick={openTosWindow} target={'tos'}>
                  terms of service.
                </Link>
              </div>
            }
            labelPlacement="end"
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.button}
            disabled={!isTosApproved}
          >
            Checkout
          </Button>
        </form>
      </div>
    </>
  );
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      registerWithFirebase: actions.registerWithFirebase,
      logoutUser: actions.logoutUser,
      checkout: actions.checkout,
      setActiveModal: actions.setActiveModal,
      getPlanData: actions.getPlanData,
    },
    dispatch,
  );
}

function mapStateToProps({ user, itemDraft, checkoutData, planData }, ownProps) {
  return {
    user,
    itemDraft,
    checkoutData,
    planData,
  };
}

export default withRouter(
  injectStripe(connect(mapStateToProps, mapDispatchToProps)(withStyles(pageStyles)(CheckoutForm))),
);
